import type { Viewer } from '../../../../viewer/viewer';
import type { IPersona } from '../../../types';
import { PointoramaSurface } from '../../../../utils/PointoramaSurface';
import { SurfacePersona } from '../../../personas/implementations/SurfacePersona';
import type { CommitContext } from './AnnotationKindAdapter';
import type { BasePlaneState, SelectionContextProvider } from './SelectionContextProvider';

/**
 * Surface-based selection context for the surface LineTool registration. Thin
 * reader: the `SurfaceHostTool` (a `SurfaceToolBase`) already owns surface
 * isolation, camera alignment, and tolerance/rotation subscriptions, so this
 * context only translates the persona's selected surface plane into the
 * kind-neutral `BasePlaneState` the subtool draws on.
 *
 * `captureZRange` is left null — the surface camera looks down a non-Z axis, so
 * a world-Z slab is meaningless; `SurfaceKindAdapter.getCaptureBoxCorners`
 * supplies the tight eye-Z range instead.
 */
export class SurfaceSelectionContext implements SelectionContextProvider {
  private onChange: (() => void) | null = null;

  constructor(
    private readonly viewer: Viewer,
    private readonly persona: IPersona,
  ) {}

  private get surfacePersona(): SurfacePersona {
    return this.persona as SurfacePersona;
  }

  attach(onChange: () => void): void {
    this.onChange = onChange;
    this.persona.addEventListener('surface_selection_changed', this.handleSelectionChanged);
    this.viewer.personaManager?.viewManager.addEventListener('view_changed', this.handleViewChanged);
  }

  detach(): void {
    this.persona.removeEventListener('surface_selection_changed', this.handleSelectionChanged);
    this.viewer.personaManager?.viewManager.removeEventListener('view_changed', this.handleViewChanged);
    this.onChange = null;
  }

  getBasePlaneState(): BasePlaneState | null {
    const surface = this.surfacePersona.getSelectedSurface();
    const identifier = this.surfacePersona.getSelectedSurfaceIdentifier();
    if (!surface || !identifier) return null;
    const plane = surface.getPlane();
    return {
      origin: plane.position.clone(),
      normal: plane.normal.clone(),
      captureZRange: null,
      contextKey: identifier,
    };
  }

  getCommitContext(): CommitContext | null {
    const basePlane = this.getBasePlaneState();
    if (!basePlane) return null;
    return { basePlane };
  }

  /** Currently selected surface, for the adapter's commit/hit-test paths. */
  getSelectedSurface(): PointoramaSurface | null {
    return this.surfacePersona.getSelectedSurface();
  }

  private handleSelectionChanged = (): void => {
    this.onChange?.();
  };

  private handleViewChanged = (): void => {
    this.onChange?.();
  };
}
